import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { portalConst } from '@app/config';
import { NotificationService } from '@app/core';
import { notNull, safeEmpty } from '@app/shared';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable, switchMap, tap } from 'rxjs';
import { SupportService } from '../../services/support.service';
import { SUPPORT_TOOLBAR } from './base';

@Component({
  templateUrl: './modal-list-report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalListReportComponent implements OnInit, OnDestroy {
  public readonly optionsTypes = [
    {
      value: 1,
      label: this._translateService.instant('suporte.tipo-feedback.sugestao'),
      class: 'text-blue-500',
      icon: 'assets/icons/icon-megaphone.svg',
    },
    {
      value: 2,
      label: this._translateService.instant('suporte.tipo-feedback.bug'),
      class: 'text-red-500',
      icon: 'assets/icons/icon-alert-img.svg',
    },
  ];

  public readonly toolbar = SUPPORT_TOOLBAR;

  public form: FormGroup;
  public optionsProdutos$: Observable<any>;
  public submitted$ = new BehaviorSubject<boolean>(false);
  public readonly urlUploadCkEditorImage = `${portalConst.api.assessments}/v1/upload/ckeditor-image?path=avaliacoes`;

  constructor(
    @Inject(DIALOG_DATA) public data: any,
    private _router: Router,
    private _dialogRef: DialogRef,
    private _store: Store<AppState>,
    private _destroyRef: DestroyRef,
    private _formBuilder: FormBuilder,
    private _reportService: SupportService,
    private _translateService: TranslateService,
    private _notificationService: NotificationService,
  ) {}

  public ngOnInit() {
    this.loadProducts();
    this.initForm();
    this.getContext();
  }

  public ngOnDestroy() {
    this.submitted$.complete();
  }

  public get isQuestionReport(): boolean {
    return this.data?.question;
  }

  public onSend() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.send();
  }

  private initForm() {
    this.form = this._formBuilder.group({
      nome: [undefined, [Validators.required, Validators.maxLength(100)]],
      telefone: [undefined, [Validators.required, Validators.maxLength(20)]],
      email: [undefined, [Validators.required, Validators.email, Validators.maxLength(100)]],
      tipo: [1, Validators.required],
      produto: [undefined, Validators.required],
      feedback: [undefined, [Validators.required, Validators.maxLength(1000)]],
      formulario: [undefined],
    });

    this.form
      .get('produto')
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((produto) => {
        switch (produto) {
          case 'assessments-manager':
            this.form.setControl('formulario', this.getFormEclassProva());
            break;
          case 'assessments-manager-question':
            this.form.setControl('formulario', this.getFormEclassProvaQuestao());
            break;
          case 'eclass':
            this.form.setControl('formulario', this.getFormEclass());
            break;
          case 'learning-trails':
            this.form.setControl('formulario', this.getFormTrilhas());
            break;
          case 'materials':
            this.form.setControl('formulario', this.getFormMateriaisDidaticos());
            break;
          case 'academic':
            this.form.setControl('formulario', this.getFormAcademico());
            break;
          case 'others':
            this.form.setControl('formulario', this.getFormOutros());
            break;
          default:
            this.form.setControl('formulario', undefined);
            break;
        }
      });
  }

  private getFormEclassProva() {
    return this._formBuilder.group({
      codigo: [undefined],
      autor: [undefined],
      unidade: [undefined],
      turma_aluno: [undefined],
    });
  }

  private getFormEclassProvaQuestao() {
    return this._formBuilder.group({
      codigo_questao: [undefined],
    });
  }

  private getFormEclass() {
    return this._formBuilder.group({
      titulo: [undefined],
      unidade: [undefined],
      autor: [undefined],
      turma_aluno: [undefined],
      disciplina: [undefined],
    });
  }

  private getFormTrilhas() {
    return this._formBuilder.group({
      titulo: [undefined],
      unidade: [undefined],
    });
  }

  private getFormMateriaisDidaticos() {
    return this._formBuilder.group({
      disciplina: [undefined],
      serie: [undefined],
      unidade: [undefined],
    });
  }

  private getFormAcademico() {
    return this._formBuilder.group({
      unidade: [undefined],
      turma_aluno: [undefined],
    });
  }

  private getFormOutros() {
    return this._formBuilder.group({
      produto: [undefined],
      unidade: [undefined],
      turma_aluno: [undefined],
    });
  }

  private loadProducts() {
    this.optionsProdutos$ = this._store.select(AppSelectors.ActiveUser).pipe(
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      map((activeUser) => activeUser.pais_id),
      switchMap((paisId) => this._reportService.getProducts(paisId)),
      safeEmpty(),
      map((products) =>
        products.filter((p) =>
          this.isQuestionReport ? p.key === 'assessments-manager-question' : p.key !== 'assessments-manager-question',
        ),
      ),
    );
  }

  private send() {
    this.submitted$.next(true);

    const formValue = { ...this.form.getRawValue(), formulario: JSON.stringify(this.form.get('formulario').value) };

    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        map((activeUser) => activeUser.pais_id),
        switchMap((paisId) => this._reportService.postFeedback(formValue, paisId)),
        tap(() => this.submitted$.next(false)),
      )
      .subscribe({
        next: () => {
          this._dialogRef.close(true);
          this._notificationService.success(this._translateService.instant('gestor-avaliacoes.sucesso-feedback'));
        },
        error: () => {
          this.submitted$.next(false);
          this._notificationService.error(this._translateService.instant('gestor-avaliacoes.falha-feedback'));
        },
      });
  }

  private getContext() {
    const routeContext = {
      'eclass-prova': 'assessments-manager',
      'eclass-prova-questao': 'assessments-manager-question',
      eclass: 'eclass',
      'trilhas-de-aprendizagem': 'learning-trails',
      'materiais-didaticos': 'materials',
      academico: 'academic',
      outros: 'others',
    };

    const currentPath = this._router.url.split('/')[1];

    if (!!routeContext[currentPath]) {
      if (this.isQuestionReport) {
        this.form.get('produto').setValue(routeContext['eclass-prova-questao']);
        this.setQuestionFormConfig();
      } else {
        this.form.get('produto').setValue(routeContext[currentPath]);
      }
    } else {
      this.form.get('produto').setValue(routeContext['outros']);
    }
  }

  private setQuestionFormConfig(): void {
    this.form.get('produto').disable();
    this.form.get('formulario.codigo_questao').disable();
    this.form.get('formulario.codigo_questao').setValue(this.data.question.code);
  }
}
